<script setup>
/**
 * PhoneNumberInput.vue
 *
 * A generic, reusable international phone-number input. It renders a PrimeVue
 * `Select` (country flag + international calling code) on the LEFT and a PrimeVue
 * `InputText` (national number) on the RIGHT, wrapped in a single PrimeVue
 * `InputGroup` so the two segments read as one control.
 *
 * The component is intentionally DOMAIN-AGNOSTIC — it contains no WhatsApp-
 * specific (or any other feature-specific) logic and hardcodes no user-facing
 * strings. Every label/placeholder arrives via props already translated by the
 * caller, so it can be reused anywhere a phone number is collected.
 *
 * ## v-model contract (E.164)
 * `modelValue` is a plain string in E.164 form (`+<callingCode><nationalDigits>`),
 * matching the backend contract `/^\+[1-9]\d{6,14}$/`. On any change to either
 * segment the national input is reduced to digits only (leading zeros are kept —
 * they matter e.g. for Italian landlines) and the value emitted is:
 *   - `''` when the national field has no digits, otherwise
 *   - `'+' + callingCode + nationalDigits`  (e.g. IT +39 + "333 123 4567" → "+393331234567").
 *
 * The parent can clear the field by setting `modelValue` to '' (or any falsy
 * value): the national number resets to '' and the country resets to the IT
 * default. Because we emit '' whenever the national field is empty, that reset
 * does not bounce back through the watcher (no feedback loop).
 *
 * ## Validity (non-blocking)
 * `isValidPhoneNumber` (libphonenumber-js) decides whether the current
 * national number + selected region form a plausible number. The result is
 * emitted via `update:valid` and, once the user has typed something, also drives
 * the InputText `:invalid` styling. Validity NEVER blocks or alters what is
 * emitted — the server stays the source of truth.
 *
 * ## Country data
 * Regions and calling codes come from libphonenumber-js (`getCountries`,
 * `getCountryCallingCode`); localized display names come from the browser
 * `Intl.DisplayNames` keyed on the active vue-i18n locale, so names are shown in
 * IT/EN and recompute when the locale changes. No country-name list is
 * hand-maintained here. Flags use the globally-imported `flag-icons` CSS
 * (`<span class="fi fi-xx">`).
 *
 * @example
 * <PhoneNumberInput
 *   v-model="phone"
 *   :national-number-placeholder="$t('phone.numberPlaceholder')"
 *   :country-label="$t('phone.countryLabel')"
 *   :search-placeholder="$t('phone.searchPlaceholder')"
 *   @update:valid="phoneValid = $event"
 * />
 *
 * @prop {String} modelValue                - E.164 phone value (two-way). Default ''.
 * @prop {String} nationalNumberPlaceholder - Placeholder for the national-number InputText.
 * @prop {String} countryLabel              - Accessible aria-label for the country Select.
 * @prop {String} searchPlaceholder         - Placeholder for the Select's filter input.
 * @emits update:modelValue - (String) the E.164 value, or '' when the number is empty.
 * @emits update:valid      - (Boolean) whether the current number is a valid phone number.
 */

import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber
} from 'libphonenumber-js'

const { locale } = useI18n()

const props = defineProps({
  /** E.164 phone value (two-way bound). Empty string when no number is set. */
  modelValue: {
    type: String,
    default: ''
  },
  /** Already-translated placeholder for the national-number field. */
  nationalNumberPlaceholder: {
    type: String,
    default: ''
  },
  /** Already-translated accessible label (aria-label) for the country Select. */
  countryLabel: {
    type: String,
    default: ''
  },
  /** Already-translated placeholder for the Select's country-filter input. */
  searchPlaceholder: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'update:valid'])

/** Fallback country when none is selected or the field is reset. */
const DEFAULT_ISO = 'IT'

/** ISO2 code of the currently selected country (Select v-model). */
const selectedIso = ref(DEFAULT_ISO)
/** Raw national-number text as typed (may contain spaces / punctuation). */
const nationalNumber = ref('')
/** True once the user has interacted, so we don't flag an untouched field. */
const touched = ref(false)

/**
 * Localized country options, rebuilt whenever the active locale changes.
 * Each entry: { iso, callingCode, name }. `Intl.DisplayNames` yields names in
 * the current UI language; entries are sorted alphabetically by localized name
 * using a locale-aware comparator. A `searchLabel` combines the name and both
 * bare/`+`-prefixed dial code so the Select filter matches either.
 */
const countryOptions = computed(() => {
  let displayNames
  try {
    displayNames = new Intl.DisplayNames([locale.value], { type: 'region' })
  } catch (err) {
    // Extremely defensive: if the runtime lacks Intl.DisplayNames for this
    // locale, fall back to showing the ISO code as the name.
    Console.warn('[PhoneNumberInput] Intl.DisplayNames unavailable, using ISO codes', err)
    displayNames = null
  }

  const options = getCountries().map((iso) => {
    const callingCode = getCountryCallingCode(iso)
    const name = displayNames?.of(iso) || iso
    return {
      iso,
      callingCode,
      name,
      // Searched by the Select filter (see filterFields): matches on the
      // country name, the bare dial code ("39") and the "+39" form.
      searchLabel: `${name} +${callingCode} ${callingCode}`
    }
  })

  return options.sort((a, b) => a.name.localeCompare(b.name, locale.value))
})

/** The full option object for the selected ISO (used for the calling code). */
const selectedCountry = computed(() =>
  countryOptions.value.find((c) => c.iso === selectedIso.value) || null
)

/** National number reduced to digits only (leading zeros preserved). */
const nationalDigits = computed(() => nationalNumber.value.replace(/\D/g, ''))

/**
 * The E.164 string for the current state: '' when there are no national digits,
 * otherwise '+' + callingCode + digits. This is exactly what is emitted.
 */
const e164Value = computed(() => {
  if (!nationalDigits.value) return ''
  const code = selectedCountry.value?.callingCode
  if (!code) return ''
  return `+${code}${nationalDigits.value}`
})

/** Whether the current number is valid per libphonenumber-js (national + region). */
const isValid = computed(() => {
  if (!nationalDigits.value || !selectedIso.value) return false
  try {
    return isValidPhoneNumber(nationalNumber.value, selectedIso.value)
  } catch (err) {
    Console.warn('[PhoneNumberInput] validity check failed', err)
    return false
  }
})

/** Show the invalid style only after interaction and only when actually invalid. */
const showInvalid = computed(() => touched.value && !!nationalDigits.value && !isValid.value)

/** Emit the current E.164 value and validity to the parent. */
function emitValue() {
  emit('update:modelValue', e164Value.value)
  emit('update:valid', isValid.value)
}

/** National-number input handler: mark touched and re-emit. */
function onNationalInput() {
  touched.value = true
  emitValue()
}

/** Country-change handler: re-emit (calling code changed → E.164 changes). */
function onCountryChange() {
  emitValue()
}

/**
 * Keep local state in sync with an externally-set modelValue. We only act on the
 * reset case (parent sets a falsy value): clear the national field and restore
 * the IT default. We deliberately do NOT try to parse a non-empty inbound value
 * back into country + national parts — the parent drives content through the
 * model only for clearing; normal edits flow outward from this component. Since
 * an empty national field emits '', this never forms a feedback loop.
 */
watch(
  () => props.modelValue,
  (val) => {
    if (!val) {
      if (nationalNumber.value !== '') nationalNumber.value = ''
      if (selectedIso.value !== DEFAULT_ISO) selectedIso.value = DEFAULT_ISO
      touched.value = false
    }
  }
)

onMounted(() => {
  // Establish the IT default and publish the initial (empty) validity so the
  // parent's state is defined from the start.
  selectedIso.value = DEFAULT_ISO
  emit('update:valid', isValid.value)
})
</script>

<template>
  <InputGroup>
    <!-- LEFT: country selector. Compact trigger (flag + dial code only) via the
         #value slot; full flag + name + dial code in the dropdown via #option.
         The filter matches name OR dial code through `filterFields`. -->
    <Select
      v-model="selectedIso"
      :options="countryOptions"
      option-value="iso"
      option-label="name"
      :filter="true"
      :filter-fields="['searchLabel']"
      :filter-placeholder="searchPlaceholder"
      :aria-label="countryLabel"
      :auto-filter-focus="true"
      :reset-filter-on-hide="true"
      class="phone-input__country"
      overlay-class="phone-input__overlay"
      @change="onCountryChange"
    >
      <!-- Compact selected value: flag + +code, no country name. -->
      <template #value>
        <span v-if="selectedCountry" class="phone-input__value flex items-center gap-2 select-none">
          <span :class="`fi fi-${selectedCountry.iso.toLowerCase()}`" class="phone-input__flag" />
          <span>+{{ selectedCountry.callingCode }}</span>
        </span>
      </template>

      <!-- Dropdown option: flag + country name + +code. -->
      <template #option="{ option }">
        <span class="phone-input__option flex items-center gap-2 select-none">
          <span :class="`fi fi-${option.iso.toLowerCase()}`" class="phone-input__flag" />
          <span class="phone-input__option-name flex-1">{{ option.name }}</span>
          <span class="phone-input__option-code">+{{ option.callingCode }}</span>
        </span>
      </template>
    </Select>

    <!-- RIGHT: national number. Holds digits/formatting only; E.164 is assembled
         in script. `inputmode="tel"` surfaces the phone keypad on mobile. -->
    <InputText
      v-model="nationalNumber"
      inputmode="tel"
      :placeholder="nationalNumberPlaceholder"
      :invalid="showInvalid"
      :aria-invalid="showInvalid"
      class="phone-input__number"
      @input="onNationalInput"
    />
  </InputGroup>
</template>

<style scoped>
/* Keep the country trigger narrow so the national field takes the remaining
   width; the national field grows to fill the group. These target custom
   layout only — no PrimeVue color/background overrides.

   NOTE on the cascade: PrimeVue's `.p-inputgroup > .p-component { flex: 1 1 auto }`
   has EQUAL specificity to this scoped `.phone-input__country` rule and wins on
   source order, so `flex-grow` alone cannot pin the trigger's width. The cap is
   instead enforced with `max-width`, which flexbox honors regardless of the
   flex-grow battle: a flex item's resolved main size is always clamped to its
   max-width, so the trigger stops at 120px even while `.p-component` tells it to
   grow. (The class sits on the `<Select>` root, i.e. `.p-select`, so this
   scoped selector applies the cap directly to that element.) */
.phone-input__country {
  flex: 0 0 auto;
  max-width: 120px;
}

.phone-input__number {
  flex: 1 1 auto;
}

/* Uniform flag box so options line up regardless of a flag's aspect ratio.
   flag-icons renders the flag as a background image on this element. */
.phone-input__flag {
  width: 1.33em;
  min-width: 1.33em;
  line-height: 1;
  border-radius: 2px;
}

/* Let the dial code sit hard against the right edge of each option row. */
.phone-input__option {
  width: 100%;
}
</style>

<!--
  UNSCOPED on purpose. The Select's dropdown panel is teleported to <body>
  (default appendTo="body"), so neither scoped CSS nor :deep() can reach it.
  We tag that panel via `overlay-class="phone-input__overlay"` on the Select.

  Why !important is required here (not a smell): on open, PrimeVue's
  alignOverlay() imperatively sets `overlay.style.minWidth = <trigger width>px`
  (primevue/select/index.mjs, the appendTo!=="self" branch) AFTER Vue applies
  our styles — with the trigger now capped at 120px, that would squeeze the
  panel to ~120px and wrap long localized country names. A non-important inline
  style loses to that later imperative assignment, but an !important stylesheet
  rule beats a non-important inline declaration, so this reliably wins. The
  highly-specific class name keeps this global rule from leaking to other Selects.
-->
<style>
.phone-input__overlay {
  min-width: 20rem !important;
}
</style>
